import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Loader2, AlertCircle, Volume2, Play, Pause, Clock, CalendarClock,
} from 'lucide-react';

const BACKEND =
  process.env.REACT_APP_BACKEND_URL ||
  (typeof window !== 'undefined' ? window.location.origin : '');

const formatClock = (ms) => {
  const total = Math.floor((ms || 0) / 1000);
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};

/**
 * Chrome-free micro-site for a shared demo preset. No auth. Signed token
 * in URL. Auto-plays the narrated demo + scrolls through the visited
 * pages as a read-only timeline synchronised with the audio.
 */
export default function DemoPreviewPage() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [playing, setPlaying] = useState(false);
  const [currentMs, setCurrentMs] = useState(0);
  const audioRef = useRef(null);
  const sessionIdRef = useRef(null);
  const lastSentPctRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${BACKEND}/api/data-management/public/demo/preview/${token}`);
        if (cancelled) return;
        setState({ loading: false, error: null, data: res.data });
        // Fire a view event — receive session_id for later progress/cta events
        try {
          const ev = await axios.post(
            `${BACKEND}/api/data-management/public/demo/preview/${token}/event`,
            { kind: 'view' },
          );
          sessionIdRef.current = ev.data?.session_id || null;
        } catch (_) { /* analytics best-effort */ }
      } catch (err) {
        const detail =
          err?.response?.status === 410
            ? 'This demo preview link has expired. Please request a new one.'
            : err?.response?.status === 404
              ? 'This demo preview link is invalid or has been revoked.'
              : err?.response?.data?.detail || 'Could not load this demo.';
        if (!cancelled) setState({ loading: false, error: detail, data: null });
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const sendProgress = (pct) => {
    const sid = sessionIdRef.current;
    if (!sid) return;
    const rounded = Math.max(0, Math.min(100, Math.round(pct)));
    // Only send when % moves up by >= 5 pts or reaches 100
    if (rounded !== 100 && rounded - lastSentPctRef.current < 5) return;
    lastSentPctRef.current = rounded;
    const url = `${BACKEND}/api/data-management/public/demo/preview/${token}/event`;
    const body = JSON.stringify({ kind: 'progress', session_id: sid, progress_pct: rounded });
    // Use sendBeacon when available for reliability on tab close/end
    if (navigator.sendBeacon) {
      try {
        const blob = new Blob([body], { type: 'application/json' });
        navigator.sendBeacon(url, blob);
        return;
      } catch (_) { /* fall through */ }
    }
    axios.post(url, { kind: 'progress', session_id: sid, progress_pct: rounded }).catch(() => {});
  };

  const sendCtaClick = () => {
    const sid = sessionIdRef.current;
    if (!sid) return;
    const url = `${BACKEND}/api/data-management/public/demo/preview/${token}/event`;
    const body = JSON.stringify({ kind: 'cta_click', session_id: sid });
    if (navigator.sendBeacon) {
      try {
        const blob = new Blob([body], { type: 'application/json' });
        navigator.sendBeacon(url, blob);
        return;
      } catch (_) { /* fall through */ }
    }
    axios.post(url, { kind: 'cta_click', session_id: sid }).catch(() => {});
  };

  // Track audio time for timeline sync + progress analytics
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;
    const onTime = () => {
      const ms = (audio.currentTime || 0) * 1000;
      setCurrentMs(ms);
      const dur = (audio.duration || 0) * 1000;
      if (dur > 0) sendProgress((ms / dur) * 100);
    };
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onEnded = () => { setPlaying(false); sendProgress(100); };
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnded);
    return () => {
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnded);
    };
  }, [state.data]);  // eslint-disable-line react-hooks/exhaustive-deps

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) a.play().catch(() => { /* autoplay may be blocked */ });
    else a.pause();
  };

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-950 text-slate-300">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-rose-950 text-slate-100 px-6">
        <div className="max-w-md text-center" data-testid="demo-preview-error">
          <AlertCircle className="h-10 w-10 text-rose-400 mx-auto mb-3" />
          <h1 className="text-xl font-semibold mb-2">Demo unavailable</h1>
          <p className="text-sm text-slate-300">{state.error}</p>
        </div>
      </div>
    );
  }

  const d = state.data;
  const events = Array.isArray(d?.events) ? d.events : [];
  const activeEventIdx = events.reduce((acc, ev, i) => (ev.t <= currentMs ? i : acc), -1);
  const expiresAt = d.expires_at ? new Date(d.expires_at) : null;

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 text-slate-100"
      data-testid="demo-preview-page"
    >
      <div className="max-w-4xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-indigo-300 mb-2">
              Acme Industries · Narrated Demo
            </div>
            <h1
              className="text-3xl sm:text-4xl font-semibold leading-tight"
              data-testid="demo-preview-title"
            >
              {d.scenario}
            </h1>
            {d.description && (
              <p className="mt-2 text-sm text-slate-300 max-w-2xl">{d.description}</p>
            )}
            <div className="mt-3 flex flex-wrap items-center gap-3 text-[11px] text-slate-400">
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" /> {formatClock(d.total_ms)}
              </span>
              <span className="inline-flex items-center gap-1">
                <Volume2 className="h-3.5 w-3.5" /> voice: {d.voice}
              </span>
              {expiresAt && (
                <span className="inline-flex items-center gap-1">
                  <CalendarClock className="h-3.5 w-3.5" />
                  expires {expiresAt.toLocaleDateString()} {expiresAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
              {(d.tags || []).map((t) => (
                <span key={t} className="rounded-full bg-indigo-500/20 border border-indigo-400/30 px-2 py-0.5 text-[10px] font-medium text-indigo-200">
                  {t}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Audio player */}
        <div
          className="rounded-2xl border border-indigo-400/20 bg-slate-900/50 backdrop-blur p-5 shadow-2xl mb-6"
          data-testid="demo-preview-player"
        >
          <div className="flex items-center gap-4">
            <button
              onClick={togglePlay}
              className="h-12 w-12 rounded-full bg-indigo-500 hover:bg-indigo-400 active:scale-95 flex items-center justify-center transition-all shadow-lg shadow-indigo-500/30"
              data-testid="demo-preview-play-btn"
              title={playing ? 'Pause' : 'Play'}
            >
              {playing ? <Pause className="h-5 w-5 text-white" /> : <Play className="h-5 w-5 text-white translate-x-0.5" />}
            </button>
            <div className="flex-1">
              <audio
                ref={audioRef}
                src={`${BACKEND}${d.audio_url}`}
                autoPlay
                controls
                className="w-full"
                data-testid="demo-preview-audio"
              >
                <track kind="captions" />
              </audio>
            </div>
          </div>
        </div>

        {/* Synchronised timeline */}
        {events.length > 0 && (
          <div className="rounded-2xl border border-slate-100 bg-slate-900/40 p-5 mb-6">
            <div className="text-[11px] uppercase tracking-[0.2em] text-indigo-300 mb-3">
              Timeline ({events.length} moments)
            </div>
            <ol className="space-y-2" data-testid="demo-preview-timeline">
              {events.map((ev, i) => {
                const mm = Math.floor((ev.t || 0) / 60000);
                const ss = String(Math.floor(((ev.t || 0) % 60000) / 1000)).padStart(2, '0');
                const isActive = i === activeEventIdx;
                const isPast = i < activeEventIdx;
                const label =
                  ev.kind === 'click'
                    ? `Click${ev.text ? `: ${ev.text}` : ''}`
                    : ev.kind === 'route'
                      ? `Navigate → ${ev.path}`
                      : ev.text || ev.kind;
                return (
                  <li
                    key={`ev-${i}-${ev.t}`}
                    className={`flex items-start gap-3 rounded-lg px-3 py-2 transition-colors ${
                      isActive
                        ? 'bg-indigo-500/20 border border-indigo-400/40'
                        : isPast
                          ? 'opacity-50'
                          : 'bg-white border border-slate-200'
                    }`}
                    data-testid={`demo-preview-event-${i}`}
                  >
                    <span className="font-mono text-xs text-indigo-200 tabular-nums w-12">
                      {mm}:{ss}
                    </span>
                    <span className="text-sm text-slate-200 flex-1 min-w-0 break-words">
                      {label}
                    </span>
                  </li>
                );
              })}
            </ol>
          </div>
        )}

        {/* Script */}
        {d.script && (
          <details className="rounded-2xl border border-slate-100 bg-slate-900/40 p-5 mb-6" data-testid="demo-preview-script-details">
            <summary className="cursor-pointer text-[11px] uppercase tracking-[0.2em] text-indigo-300 select-none">
              Full narration script
            </summary>
            <pre className="mt-3 whitespace-pre-wrap text-sm text-slate-200 font-sans leading-relaxed">
              {d.script}
            </pre>
          </details>
        )}

        {/* CTA */}
        <div className="text-center py-6">
          <p className="text-sm text-slate-300 mb-3">
            Ready to run this analysis on <span className="text-white font-semibold">your own data</span>?
          </p>
          <a
            href="mailto:hello@seertechsolutions.com?subject=Learning%20Impact%20Engine%20Pilot"
            onClick={sendCtaClick}
            className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 hover:from-indigo-400 hover:to-violet-400 px-6 py-3 text-sm font-semibold text-white transition-all shadow-lg shadow-indigo-500/30 active:scale-95"
            data-testid="demo-preview-cta-btn"
          >
            Book a pilot
          </a>
        </div>

        <div className="mt-8 text-center text-[10px] text-slate-500 uppercase tracking-[0.2em]">
          Powered by Seertech
        </div>
      </div>
    </div>
  );
}
